import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import {
  getBhojan,
  deleteBhojan,
  createBhojan,
  updateBhojan,
} from "../../../utils/api";

const BHOJAN_CATEGORIES = [
  { value: "Western", label: "Westers" },
  { value: "Indian", label: "Indiaas" },
  { value: "Hindustani", label: "Hindoestaans" },
  { value: "Smoothies", label: "Smoothies" },
  { value: "Ontbijt", label: "Ontbijt" },
];

const columns = [
  { key: "title", label: "Title" },
  { key: "category", label: "Category" },
  { key: "author", label: "Author" },
  {
    key: "status",
    label: "Status",
    render: (value) => (value ? String(value) : "draft"),
  },
  {
    key: "publishDate",
    label: "Publish Date",
    render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
];

const filters = [
  { key: "author", label: "Filter by Author", type: "search" },
  {
    key: "category",
    label: "Filter by Category",
    type: "select",
    options: BHOJAN_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
  },
];

const initialFormData = {
  title: "",
  author: "Maha Sabha",
  category: "Western",
  shortDescription: "",
  content: "",
  status: "draft",
  publishDate: "",
  imageUrlsText: "",
  videoUrlsText: "",
};

const splitLines = (raw = "") =>
  String(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function Bhojan() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [coverImage, setCoverImage] = useState("");
  const [existingMedia, setExistingMedia] = useState([]);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData(initialFormData);
    setCoverImage("");
    setExistingMedia([]);
  };

  const handleAdd = () => {
    resetForm();
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    const mediaItems = Array.isArray(item.mediaItems) ? item.mediaItems : [];
    const hostedMedia = mediaItems.filter(
      (m) => m?.url && /^https?:\/\//i.test(m.url)
    );
    setSelectedItem(item);
    setFormData({
      title: item.title || "",
      author: item.author || "Maha Sabha",
      category: BHOJAN_CATEGORIES.some((c) => c.value === item.category)
        ? item.category
        : "Western",
      shortDescription: item.shortDescription || "",
      content: item.content || "",
      status: item.status === "published" ? "published" : "draft",
      publishDate: toDateInput(item.publishDate),
      imageUrlsText: "",
      videoUrlsText: "",
    });
    setExistingMedia(hostedMedia);
    setCoverImage(item.imageUrl && /^https?:\/\//i.test(item.imageUrl) ? item.imageUrl : "");
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => handleEdit(item);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [coverFile, setCoverFile] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverImage(reader.result);
    reader.readAsDataURL(file);
  };

  const removeExistingMedia = (url) => {
    setExistingMedia((prev) => prev.filter((m) => m.url !== url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("title", formData.title.trim());
      payload.append("author", formData.author.trim() || "Maha Sabha");
      payload.append("category", formData.category);
      payload.append("shortDescription", formData.shortDescription.trim());
      payload.append("content", formData.content.trim());
      payload.append("status", formData.status);
      if (formData.publishDate) {
        payload.append(
          "publishDate",
          new Date(`${formData.publishDate}T12:00:00`).toISOString()
        );
      }

      // Preserve existing hosted media + any newly added remote URLs
      const extraImages = splitLines(formData.imageUrlsText).filter((u) =>
        /^https?:\/\//i.test(u)
      );
      const extraVideos = splitLines(formData.videoUrlsText).filter((u) =>
        /^https?:\/\//i.test(u)
      );
      if (existingMedia.length) {
        payload.append("mediaItems", JSON.stringify(existingMedia));
      }
      if (extraImages.length) {
        payload.append("imageUrls", JSON.stringify(extraImages));
      }
      if (extraVideos.length) {
        payload.append("videoUrls", JSON.stringify(extraVideos));
      }
      if (coverFile) {
        payload.append("mediaFiles", coverFile);
      } else if (coverImage && /^https?:\/\//i.test(coverImage)) {
        payload.append("imageUrl", coverImage);
      }

      if (isEditMode && selectedItem) {
        await updateBhojan(selectedItem._id, payload);
        alert("Bhojan recipe updated successfully!");
      } else {
        await createBhojan(payload);
        alert("Bhojan recipe created successfully!");
      }
      setModalOpen(false);
      setCoverFile(null);
    } catch (error) {
      alert(error.message || "Failed to save Bhojan recipe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Bhojan (Recipes)"
          fetchData={getBhojan}
          deleteData={deleteBhojan}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search recipes..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Bhojan Recipe" : "Add New Bhojan Recipe"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder="Recipe title"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  required
                >
                  {BHOJAN_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleFormChange}
                  placeholder="Author name"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Publish Date</label>
              <input
                type="date"
                name="publishDate"
                value={formData.publishDate}
                onChange={handleFormChange}
              />
            </div>
            <div className="form-group">
              <label>Short Description *</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                rows="3"
                required
                placeholder="Short summary for cards and notifications"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Content</h3>
            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleFormChange}
                rows="10"
                required
                placeholder="Ingredients and preparation steps"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {coverImage && (
                <div className="image-preview">
                  <img
                    src={coverImage}
                    alt="Preview"
                    style={{ maxWidth: "120px", maxHeight: "120px", borderRadius: "8px" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCoverImage("");
                      setCoverFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {existingMedia.length > 0 && (
              <div className="form-group">
                <label>Existing Media</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {existingMedia.map((m) => (
                    <div key={m.url} style={{ textAlign: "center" }}>
                      {m.type === "video" ? (
                        <span style={{ fontSize: 12 }}>🎬 Video</span>
                      ) : (
                        <img
                          src={m.url}
                          alt="media"
                          style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }}
                        />
                      )}
                      <button
                        type="button"
                        style={{ display: "block", marginTop: 4, fontSize: 11 }}
                        onClick={() => removeExistingMedia(m.url)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Extra Image URLs (one per line)</label>
              <textarea
                name="imageUrlsText"
                value={formData.imageUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder={"https://image-1.jpg\nhttps://image-2.jpg"}
              />
            </div>
            <div className="form-group">
              <label>Video URLs (one per line)</label>
              <textarea
                name="videoUrlsText"
                value={formData.videoUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder="https://video-1.mp4"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update Recipe" : "Create Recipe"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
